import type { TermsDocument } from '../types';

export const termsAndConditionsDocument: TermsDocument = {
  version: 2,
  lastUpdated: '8 April 2026',
  title: 'Careerbook Platform Terms of Service',
  sections: [
    {
      title: 'Important notice',
      content: [
        {
          type: 'text',
          text: 'These Platform Terms of Service govern access to and use of the Careerbook platform and related services. They contain important provisions about platform use, payments, refunds, liability, suspension, and dispute resolution. Please read them carefully.',
        },
        {
          type: 'text',
          text: 'By accessing or using Careerbook, you agree to these Terms. If you do not agree, you must not use the Services.',
        },
      ],
    },
    {
      title: '1. About these Terms',
      content: [
        {
          type: 'text',
          text: '1.1 Parties. These Terms are between you and Careerbook Enterprise Ltd (Careerbook, we, us, or our).',
        },
        {
          type: 'text',
          text: '1.2 Scope. These Terms apply to your access to and use of the Careerbook website, platform, mobile features, payment tools, event listing tools, Careerbook Live, communications tools, attendance tools, feedback tools, certificate tools, homepage features, follower features, analytics, and any related products or services we make available from time to time (together, the Services).',
        },
        {
          type: 'text',
          text: '1.3 Other documents that apply. The following also apply where relevant and are incorporated into these Terms:',
        },
        {
          type: 'bulleted',
          items: [
            'the Careerbook Host Payments and Payout Terms;',
            'the Careerbook Attendee Booking, Cancellation and Refund Terms;',
            'our Privacy Policy;',
            'our Cookie Policy;',
            'any event-specific rules, platform policies, feature notices, community rules, or service descriptions we publish from time to time.',
          ],
        },
        {
          type: 'text',
          text: 'If there is a conflict between these Platform Terms and a separate written agreement signed by Careerbook, that separate written agreement will prevail to the extent of the conflict.',
        },
        {
          type: 'text',
          text: '1.4 Authority. If you use the Services on behalf of a company, NHS Trust, hospital, university, society, charity, clinic, or other organisation, you represent and warrant that you have authority to bind that organisation and that organisation agrees to these Terms.',
        },
      ],
    },
    {
      title: '2. Definitions',
      content: [
        {
          type: 'text',
          text: 'In these Terms:',
        },
        {
          type: 'bulleted',
          items: [
            'Account means a Careerbook account.',
            'Attendee means a user who browses, books, buys, registers for, accesses, or attends an Event through the Services.',
            'Booking Fee means the Careerbook platform fee charged on or in relation to a Paid Booking.',
            'Event means any course, webinar, conference, live online session, in-person session, teaching event, simulation, training session, placement-related session, or other listing made available through the Services.',
            'Host means the person or entity that creates, publishes, manages, sells, or delivers an Event.',
            'Paid Booking means a booking, ticket, registration, reservation, or paid access right purchased through the Services.',
            'Paid Event means an Event offered for payment through the Services.',
            'Payout means money due to a Host after deduction of Booking Fees, payment processing charges, refunds, chargebacks, reserves, taxes, and any other lawful deductions.',
            'Refund Policy means the cancellation and refund rules set by the Host for a Paid Event.',
            'User, you, or your means any person or entity accessing or using the Services, including Hosts and Attendees.',
          ],
        },
      ],
    },
    {
      title: "3. Careerbook's role",
      content: [
        {
          type: 'text',
          text: '3.1 Platform role. Careerbook provides a technology platform that enables Hosts to create, promote, manage, and deliver Events and enables Attendees to discover, book, pay for, and attend Events.',
        },
        {
          type: 'text',
          text: '3.2 Careerbook is not the organiser. Unless Careerbook expressly states otherwise in writing for a specific Event, Careerbook is not the organiser, promoter, owner, presenter, tutor, venue operator, or provider of Events listed on the Services. Each Host is solely responsible for:',
        },
        {
          type: 'bulleted',
          items: [
            'the Event;',
            'the event listing and all related statements and representations;',
            'event delivery and quality;',
            'tutor, speaker, presenter, and venue arrangements;',
            'legal and regulatory compliance for the Event;',
            'all licences, permits, permissions, insurance, and consents;',
            'the Refund Policy for the Event;',
            'responding to attendee queries and complaints in the first instance.',
          ],
        },
        {
          type: 'text',
          text: '3.3 No guarantee of event performance. Careerbook does not guarantee that any Event will take place, be suitable for you, be free of changes, or be delivered as described by the Host.',
        },
      ],
    },
    {
      title: '4. Eligibility and accounts',
      content: [
        {
          type: 'text',
          text: '4.1 Eligibility. You may use the Services only if you are legally able to enter into a binding contract under applicable law.',
        },
        {
          type: 'text',
          text: '4.2 Account accuracy. You must provide accurate, current, and complete information and keep it updated.',
        },
        {
          type: 'text',
          text: '4.3 Security. You are responsible for maintaining the confidentiality of your Account credentials and for all activity carried out through your Account.',
        },
        {
          type: 'text',
          text: '4.4 Unauthorised use. You must notify us promptly if you believe your Account has been accessed without permission or otherwise compromised.',
        },
      ],
    },
    {
      title: '5. Host obligations',
      content: [
        {
          type: 'text',
          text: 'If you are a Host, you must:',
        },
        {
          type: 'bulleted',
          items: [
            'ensure all Event information is accurate, complete, and not misleading;',
            'clearly state the Event date, time, location or online format, price, and any material restrictions or requirements;',
            'set a clear Refund Policy for each Paid Event;',
            'ensure your Refund Policy and event terms comply with applicable law;',
            'obtain all permissions, licences, insurance, consents, and approvals needed for the Event;',
            'comply with all applicable consumer, health and safety, safeguarding, advertising, tax, sanctions, anti-bribery, accessibility, intellectual property, privacy, and data protection laws;',
            'respond to attendee queries, complaints, and refund requests promptly;',
            "not misuse Careerbook's communications, payment, or attendance tools.",
          ],
        },
        {
          type: 'text',
          text: 'If you are a Host selling into the UK consumer market, you are responsible for ensuring your event terms are fair, transparent, and legally compliant.',
        },
      ],
    },
    {
      title: '6. Attendee obligations',
      content: [
        {
          type: 'text',
          text: 'If you are an Attendee, you must:',
        },
        {
          type: 'bulleted',
          items: [
            'review the Event details and Refund Policy before booking;',
            'ensure that the Event is suitable for your needs before purchase;',
            'contact the Host first for Event-specific concerns, complaints, access problems, or refund requests;',
            'not use a refunded booking or cancelled ticket to access an Event;',
            'not misuse joining links, tickets, certificates, or attendance records.',
          ],
        },
      ],
    },
    {
      title: '7. Payments and regulated services wording',
      content: [
        {
          type: 'text',
          text: '7.1 Payment facilitation. Careerbook may make available payment functionality for Paid Events through one or more third-party payment service providers.',
        },
        {
          type: 'text',
          text: 'Careerbook does not represent that it provides regulated payment services to you except to the extent it is legally authorised to do so. Payment processing, acquiring, merchant settlement, and related regulated payment functions may be provided by third-party payment service providers acting under their own terms.',
        },
        {
          type: 'text',
          text: '7.2 Collection and remittance. Where Careerbook enables payment collection for a Paid Event, the Host authorises Careerbook and its payment service providers to collect, hold, route, reconcile, deduct, and remit relevant amounts in accordance with these Terms, the Host Payments and Payout Terms, and applicable provider terms.',
        },
        {
          type: 'text',
          text: '7.3 Verification and compliance. We may require Hosts and, where appropriate, Attendees to provide identity, business, tax, bank, anti-fraud, and other verification information. We may suspend bookings, payouts, or Accounts where reasonably necessary for compliance, security, fraud prevention, sanctions screening, or risk control.',
        },
      ],
    },
    {
      title: '8. Careerbook Live and digital tools',
      content: [
        {
          type: 'text',
          text: 'Careerbook may offer live-streaming, webinar, recording, breakout room, chat, attendance tracking, feedback, certificate, analytics, and communications tools.',
        },
        {
          type: 'text',
          text: 'We do not guarantee that any such feature will always be uninterrupted, error-free, secure, or available. Hosts remain responsible for the content, lawfulness, permissions, moderation, and delivery of their Events and related materials.',
        },
      ],
    },
    {
      title: '9. Acceptable use',
      content: [
        {
          type: 'text',
          text: 'You must not:',
        },
        {
          type: 'bulleted',
          items: [
            'use the Services unlawfully, fraudulently, deceptively, or abusively;',
            'publish false, misleading, defamatory, discriminatory, or infringing content;',
            'impersonate another person or entity;',
            'attempt to bypass or avoid platform fees;',
            'interfere with platform security or operation;',
            'scrape, crawl, harvest, or extract platform data by automated means without written permission;',
            'upload malware or harmful code;',
            'misuse attendee data, payment data, or access links;',
            'use the Services to collect special category personal data or other highly sensitive data unless expressly permitted by us in writing and handled lawfully.',
          ],
        },
      ],
    },
    {
      title: '10. Content and intellectual property',
      content: [
        {
          type: 'text',
          text: '10.1 Your content. You retain ownership of content you upload or submit, but you grant Careerbook a non-exclusive, worldwide, royalty-free licence to host, store, reproduce, adapt, display, distribute, and use that content as reasonably necessary to operate, improve, support, and promote the Services and the relevant Event.',
        },
        {
          type: 'text',
          text: '10.2 Your responsibility. You are solely responsible for your content and confirm that you have all rights needed to upload, publish, and use it.',
        },
        {
          type: 'text',
          text: '10.3 Careerbook property. The Services, software, platform design, graphics, trade names, branding, and Careerbook content are owned by or licensed to Careerbook and are protected by law. Except where expressly permitted, you must not copy, reverse engineer, distribute, license, sell, or exploit them.',
        },
      ],
    },
    {
      title: '11. Privacy and data protection',
      content: [
        {
          type: 'text',
          text: 'Your use of the Services is subject to our Privacy Policy and Cookie Policy.',
        },
        {
          type: 'text',
          text: 'Hosts are independently responsible for handling attendee personal data lawfully where they receive or use such data through the Services.',
        },
      ],
    },
    {
      title: '12. Suspension, removals, and termination',
      content: [
        {
          type: 'text',
          text: '12.1 Our rights. We may suspend, restrict, remove, disable, or terminate access to all or part of the Services, including content, listings, Accounts, payouts, or bookings, where we reasonably believe that:',
        },
        {
          type: 'bulleted',
          items: [
            'these Terms have been breached;',
            'a legal or regulatory issue arises;',
            'fraud, abuse, chargeback risk, or security risk is present;',
            'payment provider rules require action;',
            'content or an Event may expose Careerbook or users to harm or liability.',
          ],
        },
        {
          type: 'text',
          text: '12.2 Proportionate use of discretion. Where reasonably practicable, we will act proportionately and in good faith. However, we may take immediate action where urgent legal, fraud, security, or safety concerns arise.',
        },
        {
          type: 'text',
          text: '12.3 User termination. You may stop using the Services at any time. If you are a Host with active or future Events, you remain responsible for all obligations connected with those Events.',
        },
      ],
    },
    {
      title: '13. Disclaimers',
      content: [
        {
          type: 'text',
          text: 'To the fullest extent permitted by law, the Services are provided on an as is and as available basis.',
        },
        {
          type: 'text',
          text: 'Careerbook does not warrant that:',
        },
        {
          type: 'bulleted',
          items: [
            'the Services will meet your requirements;',
            'the Services will be uninterrupted, secure, or error-free;',
            'any Event will occur, be delivered as described, or be suitable for your needs;',
            'any Host or Attendee will perform their obligations;',
            'any certificate, attendance record, analytics report, communication, or feedback result will be complete or error-free.',
          ],
        },
        {
          type: 'text',
          text: 'Nothing in these Terms limits any non-excludable consumer rights you may have under applicable law.',
        },
      ],
    },
    {
      title: '14. Liability',
      content: [
        {
          type: 'text',
          text: '14.1 What Careerbook is not responsible for. To the fullest extent permitted by law, Careerbook is not responsible for:',
        },
        {
          type: 'bulleted',
          items: [
            'cancellation, postponement, or non-delivery of Events by Hosts;',
            'Event content, quality, legality, or suitability;',
            'host-set Refund Policies or refund decisions, except where Careerbook is expressly required under these Terms to process a refund;',
            'user travel, accommodation, opportunity, or other consequential losses;',
            'acts or omissions of Hosts, Attendees, speakers, venues, or third-party providers;',
            'bank fees, card fees, FX fees, or other third-party charges.',
          ],
        },
        {
          type: 'text',
          text: '14.2 Excluded losses. To the fullest extent permitted by law, Careerbook is not liable for indirect, special, incidental, punitive, or consequential losses, or for loss of profit, revenue, business, goodwill, reputation, opportunity, or data.',
        },
        {
          type: 'text',
          text: "14.3 Liability cap. To the fullest extent permitted by law, Careerbook's total aggregate liability arising out of or in connection with the Services shall not exceed the greater of:",
        },
        {
          type: 'bulleted',
          items: [
            'the total Booking Fees actually retained by Careerbook in relation to the relevant booking, Event, or claim in the 3 months before the event giving rise to liability; or',
            '£100.',
          ],
        },
        {
          type: 'text',
          text: '14.4 Non-excludable liability. Nothing in these Terms excludes or limits liability for:',
        },
        {
          type: 'bulleted',
          items: [
            'death or personal injury caused by negligence;',
            'fraud or fraudulent misrepresentation;',
            'any matter which cannot lawfully be excluded or limited.',
          ],
        },
      ],
    },
    {
      title: '15. Indemnity',
      content: [
        {
          type: 'text',
          text: 'You agree to indemnify and hold harmless Careerbook, its affiliates, directors, officers, employees, and agents against claims, liabilities, damages, losses, costs, and expenses, including reasonable legal fees, arising out of or connected with:',
        },
        {
          type: 'bulleted',
          items: [
            'your breach of these Terms;',
            'your Event or event content;',
            'your Refund Policy;',
            'your unlawful conduct;',
            'your infringement of third-party rights;',
            'your misuse of the Services.',
          ],
        },
        {
          type: 'text',
          text: "This indemnity does not apply to the extent a claim is caused by Careerbook's own negligence, fraud, or wilful misconduct.",
        },
      ],
    },
    {
      title: '16. Changes to the Services or Terms',
      content: [
        {
          type: 'text',
          text: 'We may update these Terms or change the Services from time to time.',
        },
        {
          type: 'text',
          text: 'If a change is material, we will take reasonable steps to notify users, such as by updating the date at the top of the document, posting a notice on the platform, or sending an email.',
        },
        {
          type: 'text',
          text: 'Your continued use of the Services after the effective date of an update means you accept the revised Terms.',
        },
      ],
    },
    {
      title: '17. Governing law and jurisdiction',
      content: [
        {
          type: 'text',
          text: 'These Terms are governed by the laws of England and Wales.',
        },
        {
          type: 'text',
          text: 'The courts of England and Wales have exclusive jurisdiction over disputes arising from or connected with these Terms or the Services, except where mandatory consumer law gives you the right to bring a claim in another UK jurisdiction.',
        },
      ],
    },
    {
      title: '18. General',
      content: [
        {
          type: 'text',
          text: 'If any provision is held invalid or unenforceable, the remaining provisions remain in effect.',
        },
        {
          type: 'text',
          text: 'A failure by Careerbook to enforce a right is not a waiver.',
        },
        {
          type: 'text',
          text: 'You may not assign your rights or obligations under these Terms without our prior written consent. Careerbook may assign its rights and obligations in connection with a reorganisation, sale, merger, or transfer of business.',
        },
        {
          type: 'text',
          text: 'These Terms form the entire agreement between you and Careerbook regarding the Services, except for any separate written agreement signed by Careerbook.',
        },
      ],
    },
    {
      title: '19. Contact',
      content: [
        {
          type: 'text',
          text: 'Careerbook Enterprise Ltd',
        },
        {
          type: 'text',
          text: 'Website: www.careerbook.co.uk',
        },
        {
          type: 'text',
          text: 'Email: info@careerbook.co.uk',
        },
        {
          type: 'text',
          text: 'Address: 5 Wingate Close, St-Leonards-On-Sea, Sussex, TN38 0SD',
        },
      ],
    },
  ],
};
